import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
} from 'react';
import Toast from '../../components/UI/Toast';

export type ToastType = 'success' | 'info' | 'error';

interface ToastContextValue {
  showToast: (message?: string, type?: ToastType) => void;
  hideToast: () => void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

interface Props {
  children?: ReactNode;
}

export const ToastProvider: React.FC<Props> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [type, setType] = useState<ToastType>('error');

  const showToast = useCallback(
    (newMessage?: string, newType: ToastType = 'error') => {
      setMessage(newMessage || 'An error has occurred');
      setType(newType);
      setIsOpen(true);
    },
    []
  );

  const hideToast = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Toast
        isOpen={isOpen}
        message={message}
        type={type}
        onClose={hideToast}
      />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
